import { Card, Col, Form, Radio, Row, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useGetThirdParty } from "../hooks/use-get-third-party";
import Input from "antd/es/input";
import VuiFormActionButton from "../../../components/form-action-button";
import { useCallback, useEffect, useState } from "react";
import VuiPageTitle from "../../../components/page-title";
import { useFormThirdParty } from "../hooks/use-form-third-party";
import { ThirdPartyFormData, ThirdPartyPayloadType } from "./interface";
import VuiSelect from "../../../components/select";
import { openNotification } from "../../../utils/helpers";
import VuiAccessible from "../../../components/accessible";
import VuiModalConfirmation from "../../../components/modal-confirmation";
import CategoryRepository from "../../../repositories/category-repository";

const { Text } = Typography;

const ThirdPartyFormModule = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const title = id
    ? t("pages.thirdParty.detail.title")
    : t("pages.thirdParty.add.title");
  const { loadThirdPartyData, isOnFetchingThirdPartyData, thirdPartyData } =
    useGetThirdParty();
  const { submitLoading, onSubmit, onDelete, deleteLoading } =
    useFormThirdParty();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [form] = Form.useForm<ThirdPartyFormData>();

  const onFinish = useCallback(
    async (data: ThirdPartyFormData) => {
      const normalizeData: ThirdPartyPayloadType = {
        ...data,
        category_id: data.category_id.value as number,
      };

      await onSubmit(normalizeData, id);
    },
    [id, onSubmit]
  );

  const handleCloseModalDelete = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (id) {
      await onDelete(id).then(() => {
        setShowDeleteModal(false);
        openNotification(
          "success",
          t("notification.success.deleteItem", {
            item: t("common.text.thirdParty"),
          })
        );
        navigate(`/other-list/third-party`);
      });
    }
  }, [id, navigate, onDelete, t]);

  useEffect(() => {
    if (id) {
      (async () => {
        await loadThirdPartyData(id, { with: "category" });
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (id && thirdPartyData) {
      form.setFieldsValue({
        ...thirdPartyData,
        category_id: {
          label: thirdPartyData?.category?.name,
          value: thirdPartyData?.category?.id,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thirdPartyData]);

  return (
    <>
      <VuiPageTitle title={title} onBackLink="/other-list/third-party" />

      <Spin size="large" spinning={isOnFetchingThirdPartyData}>
        <Form
          form={form}
          layout={"vertical"}
          initialValues={{
            is_active: true,
          }}
          onFinish={onFinish}
        >
          <Row gutter={[16, 16]}>
            <Col md={18} xs={24}>
              <Card title={t("common.text.information")}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="category_id"
                      label={t("common.form.category.label")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: t("common.form.category.label"),
                          }),
                        },
                      ]}
                    >
                      <VuiSelect
                        repository={CategoryRepository}
                        repositoryParams={{
                          types: 95,
                        }}
                        labelKey="name"
                        placeholder={t("common.form.category.placeholder")}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="name"
                      label={t("common.form.thirdPartyName.label")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: t("common.form.thirdPartyName.label"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder={t(
                          "common.form.thirdPartyName.placeholder"
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col md={6} xs={24}>
              <Card title={t("common.text.status")}>
                <Form.Item name="is_active">
                  <Radio.Group>
                    <Radio value={true}>Active</Radio>
                    <Radio value={false}>Not Active</Radio>
                  </Radio.Group>
                </Form.Item>
              </Card>
            </Col>

            <Col md={24} xs={24}>
              <VuiFormActionButton
                cancelBtnLink="/other-list/third-party"
                isLoading={submitLoading}
              />
            </Col>

            {id && (
              <VuiAccessible access="third-party.destroy">
                <Col xs={24}>
                  <Text
                    className="cursor-pointer"
                    onClick={() => setShowDeleteModal(true)}
                    title={t("common.text.delete")}
                    type="secondary"
                  >
                    {t("common.text.delete")}
                  </Text>
                </Col>
              </VuiAccessible>
            )}
          </Row>
        </Form>
      </Spin>

      <VuiModalConfirmation
        show={showDeleteModal}
        isLoading={deleteLoading}
        onSubmit={handleConfirmDelete}
        onCancel={handleCloseModalDelete}
      />
    </>
  );
};

export default ThirdPartyFormModule;
